import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  VERIFICATION_STATE,
  useSubscription,
} from '../../contexts/SubscriptionContext';
import {
  CONNECTION_STATE,
  ERROR_KIND,
} from '../../utils/connectionDiagnostics';
import { useToast } from '../Toast';
import Notice from '../common/Notice';
import './ServiceConnectionBanner.css';

const getBannerContent = ({
  connectionState,
  failureReason,
  isProPreserved,
}) => {
  if (connectionState === CONNECTION_STATE.API_UNREACHABLE) {
    return {
      description: isProPreserved
        ? 'PRO 이용권은 유지하고 있어요. 광고 차단 앱, VPN 또는 학교 보안 설정이 왓퀴즈 연결을 막았을 수 있어요.'
        : '광고 차단 앱, VPN 또는 학교 보안 설정이 왓퀴즈 연결을 막았을 수 있어요. 자동으로 다시 확인하고 있어요.',
      showHelp: true,
      title: isProPreserved ? 'PRO 이용권은 유지 중이에요' : '왓퀴즈 연결을 확인해주세요',
      variant: isProPreserved ? 'info' : 'warning',
    };
  }

  if (connectionState === CONNECTION_STATE.OFFLINE) {
    return {
      description: 'Wi-Fi나 인터넷 연결을 확인한 뒤 다시 시도해주세요.',
      showHelp: false,
      title: '인터넷 연결을 확인해주세요',
      variant: 'info',
    };
  }

  if (failureReason === ERROR_KIND.AUTH) {
    return {
      description: '로그인 정보가 만료됐을 수 있어요. 다시 로그인하면 이용권을 안전하게 확인할 수 있어요.',
      showHelp: false,
      title: '로그인을 다시 확인해주세요',
      variant: 'info',
    };
  }

  if (failureReason === ERROR_KIND.SERVER) {
    return {
      description: '왓퀴즈에서 자동으로 다시 확인하고 있어요. 잠시 후 다시 시도해주세요.',
      showHelp: false,
      title: '이용권 확인이 잠시 지연되고 있어요',
      variant: 'neutral',
    };
  }

  const isNetworkFailure = (
    failureReason === ERROR_KIND.NETWORK
    || failureReason === ERROR_KIND.TIMEOUT
  );
  return {
    description: isNetworkFailure
      ? '광고 차단 앱이나 보안 네트워크가 이용권 확인을 막았을 수 있어요.'
      : '현재 이용권 상태를 확인하지 못했어요. 잠시 후 다시 시도해주세요.',
    showHelp: isNetworkFailure,
    title: '이용권을 확인하지 못했어요',
    variant: isNetworkFailure ? 'warning' : 'neutral',
  };
};

const ServiceConnectionBanner = () => {
  const { currentUser, logout } = useAuth();
  const {
    connectionState,
    fetchSubscriptionStatus,
    loading,
    subscriptionStatus,
    verificationFailureReason,
    verificationState,
  } = useSubscription();
  const { showToast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const [isHelpExpanded, setIsHelpExpanded] = useState(false);
  const [isReloggingIn, setIsReloggingIn] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryMessage, setRetryMessage] = useState('');

  const hasConnectionProblem = (
    connectionState === CONNECTION_STATE.API_UNREACHABLE
    || connectionState === CONNECTION_STATE.OFFLINE
  );
  const hasVerificationProblem = (
    verificationState === VERIFICATION_STATE.FAILED
    || Boolean(subscriptionStatus?.verificationFailed)
  );
  const isVisible = Boolean(currentUser && (hasConnectionProblem || hasVerificationProblem));
  const isProPreserved = Boolean(
    subscriptionStatus?.isPro && subscriptionStatus?.verificationFailed
  );

  const content = useMemo(() => getBannerContent({
    connectionState,
    failureReason: verificationFailureReason,
    isProPreserved,
  }), [connectionState, verificationFailureReason, isProPreserved]);

  useEffect(() => {
    if (isVisible) return;
    setIsHelpExpanded(false);
    setRetryMessage('');
  }, [isVisible]);

  if (!isVisible) return null;

  const isBusy = loading || isRetrying;

  const handleRetry = async () => {
    if (isBusy) return;
    setIsRetrying(true);
    setRetryMessage('');

    try {
      const latest = await fetchSubscriptionStatus(true);
      if (latest && !latest.verification_failed) {
        showToast(
          latest.is_pro ? 'PRO 이용권을 확인했어요' : '이용권 확인을 마쳤어요',
          'success'
        );
        return;
      }
      setRetryMessage('아직 연결되지 않았어요. 해결 방법을 확인한 뒤 다시 시도해주세요.');
      if (content.showHelp) setIsHelpExpanded(true);
    } catch (_error) {
      setRetryMessage('아직 연결되지 않았어요. 해결 방법을 확인한 뒤 다시 시도해주세요.');
      if (content.showHelp) setIsHelpExpanded(true);
    } finally {
      setIsRetrying(false);
    }
  };

  const handleRelogin = async () => {
    if (isReloggingIn) return;
    setIsReloggingIn(true);
    setRetryMessage('');

    try {
      await logout();
      const returnPath = encodeURIComponent(`${location.pathname}${location.search || ''}`);
      navigate(`/auth?redirect=${returnPath}`);
    } catch (_error) {
      setRetryMessage('로그아웃하지 못했어요. 페이지를 새로고침한 뒤 다시 시도해주세요.');
      setIsReloggingIn(false);
    }
  };

  const action = verificationFailureReason === ERROR_KIND.AUTH
    ? {
      label: isReloggingIn ? '로그아웃 중' : '다시 로그인',
      onClick: handleRelogin,
      disabled: isReloggingIn,
    }
    : {
      label: isRetrying ? '확인 중' : (loading ? '자동 확인 중' : '다시 확인'),
      onClick: handleRetry,
      disabled: isBusy,
    };

  const hasFooter = content.showHelp || Boolean(retryMessage);
  const footer = hasFooter ? (
    <div className="service-connection-banner__footer">
      {content.showHelp && (
        <button
          aria-controls="service-connection-help"
          aria-expanded={isHelpExpanded}
          className="service-connection-banner__help-toggle"
          onClick={() => setIsHelpExpanded((current) => !current)}
          type="button"
        >
          {isHelpExpanded ? '해결 방법 접기' : '해결 방법 보기'}
        </button>
      )}

      {isHelpExpanded && content.showHelp && (
        <div className="service-connection-banner__help" id="service-connection-help">
          <ol>
            <li>유니콘 Pro 또는 사용 중인 광고 차단 앱을 열어주세요.</li>
            <li>
              왓퀴즈를 사용하는 동안 보호를 잠시 끄거나,
              {' '}
              <strong>차단 및 허용</strong>
              에서 왓퀴즈 연결을 허용해주세요.
            </li>
            <li>이 화면으로 돌아와 <strong>다시 확인</strong>을 눌러주세요.</li>
          </ol>
          <a
            className="service-connection-banner__help-link"
            href="/support/faq/technical-004"
            rel="noopener noreferrer"
            target="_blank"
          >
            기기별 자세한 방법
          </a>
        </div>
      )}

      {retryMessage && (
        <p className="service-connection-banner__retry-message">{retryMessage}</p>
      )}
    </div>
  ) : null;

  return (
    <div className="service-connection-banner-shell">
      <Notice
        action={action}
        className="service-connection-banner"
        footer={footer}
        role="status"
        testId="service-connection-banner"
        title={content.title}
        variant={content.variant}
      >
        {content.description}
      </Notice>
    </div>
  );
};

export default ServiceConnectionBanner;
